/**
 * LocalStore — remembers the user's last-used StampingConfig across sessions.
 *
 * This is the only piece of the original Sprint 1 `src/lib/*` seam plan that
 * survives the Mac-funnel pivot. There is no server, no account, and no
 * entitlement on web v1, so the `api/` and `entitlement/` seams were removed.
 * Persistence is a single localStorage key; nothing else is stored.
 */

import {
  defaultStampingConfig,
  STAMP_POSITIONS,
  STAMPING_THEMES,
  type StampingConfig,
} from "../../engine/models";

const STORAGE_KEY = "imprint-web:config";
const MIN_OPACITY = 0.05;
const MAX_OPACITY = 1;

export interface ConfigStore {
  /** Returns the persisted config, or the defaults if none/invalid. */
  load(): StampingConfig;
  save(config: StampingConfig): void;
  clear(): void;
}

/**
 * Create a config store backed by a Web Storage (localStorage by default).
 * The storage is injectable so it can be unit-tested without a DOM.
 */
export function createLocalStore(storage: Storage = localStorage): ConfigStore {
  return {
    load(): StampingConfig {
      let raw: string | null = null;
      try {
        raw = storage.getItem(STORAGE_KEY);
      } catch {
        return defaultStampingConfig();
      }
      if (!raw) return defaultStampingConfig();

      try {
        return sanitize(JSON.parse(raw) as unknown);
      } catch {
        return defaultStampingConfig();
      }
    },

    save(config: StampingConfig): void {
      try {
        storage.setItem(STORAGE_KEY, JSON.stringify(sanitize(config)));
      } catch {
        // Storage can throw (private mode, quota). Persistence is a
        // convenience, not a requirement — swallow and move on.
      }
    },

    clear(): void {
      try {
        storage.removeItem(STORAGE_KEY);
      } catch {
        // ignore
      }
    },
  };
}

/**
 * Coerce arbitrary parsed JSON into a valid StampingConfig by layering known
 * fields over the defaults and validating enums/ranges. Anything missing,
 * unknown, or out of range falls back to the default for that field — so a
 * stale or hand-edited localStorage value can never produce an invalid config.
 */
function sanitize(value: unknown): StampingConfig {
  const defaults = defaultStampingConfig();
  if (typeof value !== "object" || value === null) return defaults;
  const v = value as Record<string, unknown>;

  return {
    prefix: typeof v.prefix === "string" ? v.prefix : defaults.prefix,
    startNumber:
      Number.isFinite(v.startNumber) && (v.startNumber as number) >= 0
        ? Math.floor(v.startNumber as number)
        : defaults.startNumber,
    digitCount:
      Number.isFinite(v.digitCount) && (v.digitCount as number) >= 1
        ? Math.floor(v.digitCount as number)
        : defaults.digitCount,
    position: STAMP_POSITIONS.includes(v.position as never)
      ? (v.position as StampingConfig["position"])
      : defaults.position,
    theme: STAMPING_THEMES.includes(v.theme as never)
      ? (v.theme as StampingConfig["theme"])
      : defaults.theme,
    opacity: Number.isFinite(v.opacity)
      ? Math.min(Math.max(v.opacity as number, MIN_OPACITY), MAX_OPACITY)
      : defaults.opacity,
    endorsement: typeof v.endorsement === "string" ? v.endorsement : defaults.endorsement,
  };
}
